import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import PageHero from 'tio-ui/components/page-hero';
import SectionHeader from 'tio-ui/components/section-header';
import Chip from 'tio-ui/components/chip';
import {
  PhArrowLeft,
  PhTag,
  PhCheckCircle,
  PhWarning,
  PhXCircle,
  PhInfo,
  PhStar,
  PhLightning,
  PhHeart,
  PhClock,
  PhTrendUp,
  PhPalette,
  PhArrowsOutSimple,
  PhListBullets,
  PhTable,
  PhCode,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    {{! Back navigation }}
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="tio-inline-row gap-2 tio-fg-brand dark:text-violet-400 hover:tio-fg-brand dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    {{! Header }}
    <PageHero
      @title="Chip"
      @subtitle="Lightweight status badges and tags for cards, tables, and list items. Zero Frontile — pure Tailwind."
      @icon={{PhTag}}
      @gradient="cool"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">
          <PhLightning @size={{14}} class="mr-1" />
          Zero Frontile
        </Chip>
      </:actions>
    </PageHero>

    {{! ================================================================
          INTENTS
          ================================================================ }}
    <SectionHeader
      @title="Intents"
      @subtitle="Five semantic intents for contextual meaning."
      @icon={{PhPalette}}
    />
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200">
      <div class="tio-wrap">
        <Chip @intent="default">Default</Chip>
        <Chip @intent="primary">Primary</Chip>
        <Chip @intent="success">Success</Chip>
        <Chip @intent="warning">Warning</Chip>
        <Chip @intent="danger">Danger</Chip>
      </div>
    </div>

    {{! ================================================================
          SIZES
          ================================================================ }}
    <SectionHeader
      @title="Sizes"
      @subtitle="Three sizes: sm, md, lg."
      @icon={{PhArrowsOutSimple}}
    />
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200">
      <div class="tio-row flex-wrap">
        <Chip @intent="primary" @size="sm">Small</Chip>
        <Chip @intent="primary" @size="md">Medium</Chip>
        <Chip @intent="primary" @size="lg">Large</Chip>
      </div>
    </div>

    {{! ================================================================
          WITH ICONS
          ================================================================ }}
    <SectionHeader
      @title="With Icons"
      @subtitle="Chips support block content — drop in Phosphor icons, emojis, or any inline element."
      @icon={{PhStar}}
    />
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200">
      <div class="tio-wrap">
        <Chip @intent="success" @size="sm">
          <PhCheckCircle @size={{14}} />
          Active
        </Chip>
        <Chip @intent="warning" @size="sm">
          <PhClock @size={{14}} />
          Pending
        </Chip>
        <Chip @intent="danger" @size="sm">
          <PhXCircle @size={{14}} />
          Rejected
        </Chip>
        <Chip @intent="primary" @size="sm">
          <PhInfo @size={{14}} />
          In Review
        </Chip>
        <Chip @intent="default" @size="sm">
          <PhStar @size={{14}} />
          Featured
        </Chip>
      </div>
    </div>

    {{! ================================================================
          REAL-WORLD PATTERNS
          ================================================================ }}
    <SectionHeader
      @title="Real-World Patterns"
      @subtitle="Common chip usage patterns from the reimagined pages."
      @icon={{PhListBullets}}
    />

    {{! Loan Status Tags }}
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200 space-y-4">
      <p class="tio-text-sm-medium tio-fg-muted uppercase tracking-wider">Loan Status Tags</p>
      <div class="tio-wrap">
        <Chip @intent="success" @size="sm">
          <PhCheckCircle @size={{14}} />
          5 Enrolled
        </Chip>
        <Chip @intent="primary" @size="sm">
          <PhTrendUp @size={{14}} />
          12 Eligible
        </Chip>
        <Chip @intent="warning" @size="sm">
          <PhWarning @size={{14}} />
          3 Needs Attention
        </Chip>
      </div>

      <p class="tio-text-sm-medium tio-fg-muted uppercase tracking-wider pt-4">Plan Steps</p>
      <div class="tio-wrap-tight">
        <Chip @intent="success" @size="sm">
          <PhCheckCircle @size={{14}} />
          Step 1
        </Chip>
        <Chip @intent="success" @size="sm">
          <PhCheckCircle @size={{14}} />
          Step 2
        </Chip>
        <Chip @intent="primary" @size="sm">
          <PhLightning @size={{14}} />
          Step 3 — Current
        </Chip>
        <Chip @intent="default" @size="sm">Step 4</Chip>
        <Chip @intent="default" @size="sm">Step 5</Chip>
      </div>

      <p class="tio-text-sm-medium tio-fg-muted uppercase tracking-wider pt-4">Category Labels</p>
      <div class="tio-wrap-tight">
        <Chip @intent="primary" @size="sm">
          <PhHeart @size={{14}} />
          PSLF
        </Chip>
        <Chip @intent="success" @size="sm">IDR</Chip>
        <Chip @intent="warning" @size="sm">LRAP</Chip>
        <Chip @intent="danger" @size="sm">Default Risk</Chip>
        <Chip @intent="default" @size="sm">General</Chip>
      </div>
    </div>

    {{! ================================================================
          ALL COMBINATIONS
          ================================================================ }}
    <SectionHeader
      @title="Intent × Size Matrix"
      @subtitle="Every combination of intent and size."
      @icon={{PhTable}}
    />
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200 overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="tio-text-xs-medium tio-fg-muted uppercase tracking-wider">
            <th class="text-left pb-3 pr-4">Intent</th>
            <th class="text-left pb-3 px-4">Small</th>
            <th class="text-left pb-3 px-4">Medium</th>
            <th class="text-left pb-3 px-4">Large</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr>
            <td class="py-3 pr-4 text-sm tio-fg-secondary">Default</td>
            <td class="py-3 px-4"><Chip @intent="default" @size="sm">default/sm</Chip></td>
            <td class="py-3 px-4"><Chip @intent="default" @size="md">default/md</Chip></td>
            <td class="py-3 px-4"><Chip @intent="default" @size="lg">default/lg</Chip></td>
          </tr>
          <tr>
            <td class="py-3 pr-4 text-sm tio-fg-secondary">Primary</td>
            <td class="py-3 px-4"><Chip @intent="primary" @size="sm">primary/sm</Chip></td>
            <td class="py-3 px-4"><Chip @intent="primary" @size="md">primary/md</Chip></td>
            <td class="py-3 px-4"><Chip @intent="primary" @size="lg">primary/lg</Chip></td>
          </tr>
          <tr>
            <td class="py-3 pr-4 text-sm tio-fg-secondary">Success</td>
            <td class="py-3 px-4"><Chip @intent="success" @size="sm">success/sm</Chip></td>
            <td class="py-3 px-4"><Chip @intent="success" @size="md">success/md</Chip></td>
            <td class="py-3 px-4"><Chip @intent="success" @size="lg">success/lg</Chip></td>
          </tr>
          <tr>
            <td class="py-3 pr-4 text-sm tio-fg-secondary">Warning</td>
            <td class="py-3 px-4"><Chip @intent="warning" @size="sm">warning/sm</Chip></td>
            <td class="py-3 px-4"><Chip @intent="warning" @size="md">warning/md</Chip></td>
            <td class="py-3 px-4"><Chip @intent="warning" @size="lg">warning/lg</Chip></td>
          </tr>
          <tr>
            <td class="py-3 pr-4 text-sm tio-fg-secondary">Danger</td>
            <td class="py-3 px-4"><Chip @intent="danger" @size="sm">danger/sm</Chip></td>
            <td class="py-3 px-4"><Chip @intent="danger" @size="md">danger/md</Chip></td>
            <td class="py-3 px-4"><Chip @intent="danger" @size="lg">danger/lg</Chip></td>
          </tr>
        </tbody>
      </table>
    </div>

    {{! ================================================================
          API REFERENCE
          ================================================================ }}
    <SectionHeader @title="API Reference" @subtitle="Arguments and types." @icon={{PhCode}} />
    <div class="tio-bg-surface rounded-xl p-6 ring-1 ring-gray-200 overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="tio-text-xs-medium tio-fg-muted uppercase tracking-wider">
            <th class="text-left pb-3 pr-4">Arg</th>
            <th class="text-left pb-3 px-4">Type</th>
            <th class="text-left pb-3 px-4">Default</th>
            <th class="text-left pb-3 px-4">Description</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          <tr>
            <td class="py-2 pr-4 font-mono text-xs tio-fg-primary">@intent</td>
            <td class="py-2 px-4 font-mono text-xs tio-fg-secondary">'default' | 'primary' |
              'success' | 'warning' | 'danger'</td>
            <td class="py-2 px-4 font-mono text-xs tio-fg-secondary">'default'</td>
            <td class="py-2 px-4 tio-fg-secondary">Visual intent / semantic color</td>
          </tr>
          <tr>
            <td class="py-2 pr-4 font-mono text-xs tio-fg-primary">@size</td>
            <td class="py-2 px-4 font-mono text-xs tio-fg-secondary">'sm' | 'md' | 'lg'</td>
            <td class="py-2 px-4 font-mono text-xs tio-fg-secondary">'md'</td>
            <td class="py-2 px-4 tio-fg-secondary">Size of the chip</td>
          </tr>
          <tr>
            <td class="py-2 pr-4 font-mono text-xs tio-fg-primary">...attributes</td>
            <td
              class="py-2 px-4 font-mono text-xs tio-fg-secondary"
            >HTMLAttributes&lt;HTMLSpanElement&gt;</td>
            <td class="py-2 px-4 font-mono text-xs tio-fg-secondary">—</td>
            <td class="py-2 px-4 tio-fg-secondary">Splattributes forwarded to the root span</td>
          </tr>
        </tbody>
      </table>
    </div>
  </VStack>
</template>
